import { usePackageModalContext } from '@components/detailed-pricing/contexts/PackageModalContext';
import { usePackageServices } from '@components/detailed-pricing/hooks/use-package-service-item';
import { Icon } from '@tkww/orion-web-ds-graphic';
import { TextBody } from '@tkww/orion-web-ds-text';
import React, { memo, useMemo } from 'react';
import type { DetailedPricingPackage } from '../../jotai/storefront/detailed-pricing';
import Styles from './packageServiceItem.scss';

const VISIBLE_SERVICES_COUNT = 3;

type ServicesMetaProps = {
	serviceDuration: string;
	numberOfGuests?: number;
};

const ServicesMeta = ({
	serviceDuration,
	numberOfGuests,
}: ServicesMetaProps) => (
	<>
		{numberOfGuests && (
			<li className={Styles.serviceListItem}>
				<Icon name="couple" size="sm" aria-label="Number of guests" />
				<TextBody size="sm">{numberOfGuests} guests</TextBody>
			</li>
		)}
		{serviceDuration && (
			<li className={Styles.serviceListItem}>
				<Icon name="clock" size="sm" aria-label="Duration" />
				<TextBody size="sm">{serviceDuration}</TextBody>
			</li>
		)}
	</>
);

const Items = memo(function Items({
	items,
}: {
	items: { type: 'group' | 'service'; group: string; service?: string }[];
}) {
	return (
		<>
			{items.map((item, index) =>
				item.type === 'group' ? (
					<li
						key={`group-${item.group}-${index}`}
						className={Styles.serviceListItem}
					>
						<TextBody size="sm" bold>
							{item.group}
						</TextBody>
					</li>
				) : (
					<li
						key={`service-${item.group}-${item.service}-${index}`}
						className={Styles.serviceListItem}
					>
						<Icon
							color="success"
							name="check"
							size="sm"
							aria-label={undefined}
							aria-hidden={true}
						/>
						<TextBody size="sm">{item.service}</TextBody>
					</li>
				),
			)}
		</>
	);
});

export type ServiceListProps = {
	pkg: DetailedPricingPackage;
	testId?: string;
	showLimited?: boolean;
};

export const PackageServiceItems = ({
	pkg,
	testId = 'package-service-items-expand-button',
	showLimited = true,
}: ServiceListProps) => {
	const services = usePackageServices(pkg);
	const packageModalContext = usePackageModalContext();

	const { renderedItems, totalServicesCount, visibleCount } = useMemo(() => {
		const allServicesCount = Object.values(services).reduce(
			(acc, arr) => acc + arr.length,
			0,
		);

		const limitCount = showLimited ? VISIBLE_SERVICES_COUNT : undefined;
		let count = 0;
		const items: Array<{
			type: 'group' | 'service';
			group: string;
			service?: string;
		}> = [];

		for (const [group, serviceList] of Object.entries(services)) {
			if (limitCount !== undefined && count >= limitCount) break;

			items.push({ type: 'group', group });

			for (const service of serviceList) {
				if (limitCount !== undefined && count >= limitCount) break;
				items.push({ type: 'service', group, service });
				count++;
			}
		}

		return {
			renderedItems: items,
			totalServicesCount: allServicesCount,
			visibleCount: count,
		};
	}, [services, showLimited]);

	const hiddenCount = totalServicesCount - visibleCount;

	return (
		<>
			<ServicesMeta
				serviceDuration={pkg.serviceDuration}
				numberOfGuests={pkg.numberOfGuests ?? undefined}
			/>
			<Items items={renderedItems} />
			{showLimited && hiddenCount > 0 && (
				<li className={Styles.serviceListItem}>
					<TextBody
						as="button"
						onClick={packageModalContext?.openModal}
						className={Styles.showMoreButton}
						bold
						size="sm"
						data-testid={testId}
					>
						+{hiddenCount} more
					</TextBody>
				</li>
			)}
		</>
	);
};
